/**
 * URL Analysis Service
 * Provides intelligent URL parsing, country detection, and category identification
 */

export interface UrlAnalysis {
  domain: string;
  suggestedCountry: string | null;
  category: ProductCategory | null;
  confidence: 'high' | 'medium' | 'low';
  detectionMethod: 'domain' | 'pattern' | 'tld' | 'marketplace';
  alternativeDomain?: string;
  categoryPrompts?: CategoryPrompts;
}

export interface CategoryPrompts {
  notesPlaceholder: string;
  tips: string[];
}

export type ProductCategory = 
  | 'fashion'
  | 'electronics'
  | 'footwear'
  | 'books'
  | 'home'
  | 'beauty'
  | 'sports'
  | 'luxury'
  | 'toys'
  | 'baby'
  | 'general';

// Domain to country mapping - Enterprise-grade international coverage
const DOMAIN_COUNTRY_MAP: Record<string, string> = {
  // Amazon domains - Complete global coverage
  'amazon.com': 'US',
  'amazon.in': 'IN',
  'amazon.co.uk': 'GB',
  'amazon.ca': 'CA',
  'amazon.de': 'DE',
  'amazon.fr': 'FR',
  'amazon.es': 'ES',
  'amazon.it': 'IT',
  'amazon.co.jp': 'JP',
  'amazon.com.au': 'AU',
  'amazon.ae': 'AE',
  'amazon.sa': 'SA',
  'amazon.com.br': 'BR',
  'amazon.com.mx': 'MX',
  'amazon.sg': 'SG',
  'amazon.nl': 'NL',
  'amazon.se': 'SE',
  'amazon.com.tr': 'TR',
  'amazon.eg': 'EG',
  'amazon.pl': 'PL',
  
  // eBay domains - Global marketplace coverage
  'ebay.com': 'US',
  'ebay.co.uk': 'GB',
  'ebay.de': 'DE',
  'ebay.ca': 'CA',
  'ebay.com.au': 'AU',
  'ebay.fr': 'FR',
  'ebay.it': 'IT',
  'ebay.es': 'ES',
  'ebay.in': 'IN',
  'ebay.com.sg': 'SG',
  'ebay.com.my': 'MY',
  'ebay.ie': 'IE',
  'ebay.at': 'AT',
  'ebay.ch': 'CH',
  'ebay.be': 'BE',
  'ebay.nl': 'NL',
  
  // Regional marketplaces - India
  'flipkart.com': 'IN',
  'myntra.com': 'IN',
  'ajio.com': 'IN',
  'nykaa.com': 'IN',
  'snapdeal.com': 'IN',
  'shopclues.com': 'IN',
  'paytmmall.com': 'IN',
  'bigbasket.com': 'IN',
  'grofers.com': 'IN',
  'zomato.com': 'IN',
  'swiggy.com': 'IN',
  
  // Regional marketplaces - China
  'alibaba.com': 'CN',
  'aliexpress.com': 'CN',
  'taobao.com': 'CN',
  'tmall.com': 'CN',
  'jd.com': 'CN',
  '1688.com': 'CN',
  'pinduoduo.com': 'CN',
  'vip.com': 'CN',
  'suning.com': 'CN',
  'dangdang.com': 'CN',
  
  // North America - USA/Canada
  'walmart.com': 'US',
  'walmart.ca': 'CA',
  'target.com': 'US',
  'bestbuy.com': 'US',
  'bestbuy.ca': 'CA',
  'costco.com': 'US',
  'costco.ca': 'CA',
  'etsy.com': 'US',
  'newegg.com': 'US',
  'newegg.ca': 'CA',
  'homedepot.com': 'US',
  'homedepot.ca': 'CA',
  'lowes.com': 'US',
  'macys.com': 'US',
  'nordstrom.com': 'US',
  'wayfair.com': 'US',
  'overstock.com': 'US',
  
  // Southeast Asia - Major platforms
  'shopee.sg': 'SG',
  'shopee.co.id': 'ID',
  'shopee.co.th': 'TH',
  'shopee.ph': 'PH',
  'shopee.vn': 'VN',
  'shopee.my': 'MY',
  'shopee.tw': 'TW',
  'lazada.sg': 'SG',
  'lazada.co.id': 'ID',
  'lazada.co.th': 'TH',
  'lazada.com.ph': 'PH',
  'lazada.vn': 'VN',
  'lazada.com.my': 'MY',
  'tokopedia.com': 'ID',
  'bukalapak.com': 'ID',
  'blibli.com': 'ID',
  'qoo10.sg': 'SG',
  'qoo10.my': 'MY',
  
  // Fashion brands - Global
  'zara.com': 'ES',
  'hm.com': 'SE',
  'uniqlo.com': 'JP',
  'nike.com': 'US',
  'adidas.com': 'DE',
  'puma.com': 'DE',
  'reebok.com': 'US',
  'underarmour.com': 'US',
  'forever21.com': 'US',
  'gap.com': 'US',
  'oldnavy.com': 'US',
  'asos.com': 'GB',
  'boohoo.com': 'GB',
  'shein.com': 'CN',
  'zalando.com': 'DE',
  'zalando.de': 'DE',
  'zalando.fr': 'FR',
  'zalando.co.uk': 'GB',
  'zalando.it': 'IT',
  'zalando.es': 'ES',
  'zalando.nl': 'NL',
  'zalando.be': 'BE',
  'zalando.at': 'AT',
  'zalando.ch': 'CH',
  'zalando.pl': 'PL',
  'zalando.se': 'SE',
  'zalando.dk': 'DK',
  'zalando.fi': 'FI',
  'zalando.no': 'NO',
  
  // European fashion
  'aboutyou.com': 'DE',
  'aboutyou.de': 'DE',
  'aboutyou.at': 'AT',
  'aboutyou.ch': 'CH',
  'lamoda.ru': 'RU',
  'wildberries.ru': 'RU',
  'farfetch.com': 'GB',
  'net-a-porter.com': 'GB',
  'mrporter.com': 'GB',
  'matchesfashion.com': 'GB',
  'ssense.com': 'CA',
  
  // Luxury fashion brands
  'prada.com': 'IT',
  'ysl.com': 'FR',
  'balenciaga.com': 'FR',
  'dior.com': 'FR',
  'chanel.com': 'FR',
  'gucci.com': 'IT',
  'hermes.com': 'FR',
  'louisvuitton.com': 'FR',
  'bottegaveneta.com': 'IT',
  'versace.com': 'IT',
  'armani.com': 'IT',
  'dolcegabbana.com': 'IT',
  'valentino.com': 'IT',
  'fendi.com': 'IT',
  'givenchy.com': 'FR',
  'celine.com': 'FR',
  'loewe.com': 'ES',
  'burberry.com': 'GB',
  
  // Electronics - Global brands
  'apple.com': 'US',
  'samsung.com': 'KR',
  'sony.com': 'JP',
  'lg.com': 'KR',
  'dell.com': 'US',
  'hp.com': 'US',
  'lenovo.com': 'CN',
  'asus.com': 'TW',
  'acer.com': 'TW',
  'msi.com': 'TW',
  'gigabyte.com': 'TW',
  'xiaomi.com': 'CN',
  'huawei.com': 'CN',
  'oppo.com': 'CN',
  'vivo.com': 'CN',
  'oneplus.com': 'CN',
  'realme.com': 'CN',
  'honor.com': 'CN',
  'motorola.com': 'US',
  'nokia.com': 'FI',
  'blackberry.com': 'CA',
  
  // Home & Garden
  'ikea.com': 'SE',
  'wayfair.com': 'US',
  'wayfair.ca': 'CA',
  'wayfair.co.uk': 'GB',
  'wayfair.de': 'DE',
  'homedepot.com': 'US',
  'lowes.com': 'US',
  'rona.ca': 'CA',
  'canadiantire.ca': 'CA',
  'bunnings.com.au': 'AU',
  'castorama.fr': 'FR',
  'leroy-merlin.fr': 'FR',
  'bauhaus.info': 'DE',
  'obi.de': 'DE',
  'hornbach.de': 'DE',
  
  // Beauty & Cosmetics
  'sephora.com': 'US',
  'sephora.ca': 'CA',
  'sephora.fr': 'FR',
  'sephora.it': 'IT',
  'sephora.com.au': 'AU',
  'ulta.com': 'US',
  'nykaa.com': 'IN',
  'beautybay.com': 'GB',
  'lookfantastic.com': 'GB',
  'strawberrynet.com': 'HK',
  'yesstyle.com': 'HK',
  
  // Sports & Outdoor
  'decathlon.com': 'FR',
  'sportsdirect.com': 'GB',
  'dickssportinggoods.com': 'US',
  'rei.com': 'US',
  'patagonia.com': 'US',
  'thenorthface.com': 'US',
  'columbia.com': 'US',
  'backcountry.com': 'US',
  'mountainwarehouse.com': 'GB',
  'intersport.com': 'FR',
  
  // Books & Media
  'barnesandnoble.com': 'US',
  'bookdepository.com': 'GB',
  'waterstones.com': 'GB',
  'chapters.indigo.ca': 'CA',
  'fnac.com': 'FR',
  'thalia.de': 'DE',
  'bol.com': 'NL',
  'bookshop.org': 'US',
  
  // Toys & Kids
  'toysrus.com': 'US',
  'toysrus.ca': 'CA',
  'smythstoys.com': 'GB',
  'carters.com': 'US',
  'gymboree.com': 'US',
  'babiesrus.com': 'US',
  'mothercare.com': 'GB',
  'firstcry.com': 'IN',
  
  // Regional - Middle East
  'noon.com': 'AE',
  'souq.com': 'AE',
  'namshi.com': 'AE',
  'sivvi.com': 'AE',
  'mumzworld.com': 'AE',
  
  // Regional - Latin America
  'mercadolibre.com.ar': 'AR',
  'mercadolibre.com.mx': 'MX',
  'mercadolibre.com.br': 'BR',
  'mercadolibre.cl': 'CL',
  'mercadolibre.com.co': 'CO',
  'mercadolibre.com.pe': 'PE',
  'americanas.com.br': 'BR',
  'submarino.com.br': 'BR',
  'magazineluiza.com.br': 'BR',
  'casasbahia.com.br': 'BR',
  'extra.com.br': 'BR',
  
  // Regional - Africa
  'jumia.com.ng': 'NG',
  'jumia.co.ke': 'KE',
  'jumia.com.eg': 'EG',
  'jumia.ma': 'MA',
  'konga.com': 'NG',
  'takealot.com': 'ZA',
  
  // Regional - Australia/New Zealand
  'catch.com.au': 'AU',
  'kogan.com': 'AU',
  'jbhifi.com.au': 'AU',
  'bunnings.com.au': 'AU',
  'woolworths.com.au': 'AU',
  'coles.com.au': 'AU',
  'thewarehouse.co.nz': 'NZ',
  'trademe.co.nz': 'NZ',
};

// Category detection patterns
const CATEGORY_PATTERNS: Record<ProductCategory, { domains: string[], keywords: string[] }> = {
  fashion: {
    domains: ['myntra.com', 'ajio.com', 'zara.com', 'hm.com', 'uniqlo.com', 'forever21.com', 'gap.com', 'asos.com', 'boohoo.com', 'shein.com'],
    keywords: ['clothing', 'apparel', 'fashion', 'dress', 'shirt', 'pants', 'jeans', 'jacket']
  },
  luxury: {
    domains: ['prada.com', 'ysl.com', 'balenciaga.com', 'dior.com', 'chanel.com'],
    keywords: ['luxury', 'haute couture', 'designer', 'premium', 'handbag', 'fragrance', 'jewelry', 'runway']
  },
  toys: {
    domains: ['toysrus.com'],
    keywords: ['toys', 'games', 'playset', 'action figure', 'doll', 'building blocks', 'educational', 'puzzle']
  },
  baby: {
    domains: ['carters.com'],
    keywords: ['baby', 'infant', 'toddler', 'newborn', 'onesie', 'sleepwear', 'organic cotton', 'baby clothes']
  },
  footwear: {
    domains: ['nike.com', 'adidas.com', 'puma.com', 'reebok.com', 'underarmour.com'],
    keywords: ['shoes', 'sneakers', 'boots', 'sandals', 'footwear', 'trainers', 'running']
  },
  electronics: {
    domains: ['bestbuy.com', 'newegg.com', 'apple.com', 'samsung.com', 'sony.com', 'lg.com', 'dell.com', 'hp.com'],
    keywords: ['laptop', 'phone', 'tablet', 'computer', 'monitor', 'keyboard', 'mouse', 'headphones', 'camera', 'tv', 'television']
  },
  books: {
    domains: ['bookdepository.com', 'barnesandnoble.com'],
    keywords: ['book', 'novel', 'textbook', 'kindle', 'paperback', 'hardcover']
  },
  home: {
    domains: ['ikea.com', 'wayfair.com', 'homedepot.com', 'lowes.com'],
    keywords: ['furniture', 'decor', 'kitchen', 'bathroom', 'bedding', 'lighting']
  },
  beauty: {
    domains: ['sephora.com', 'ulta.com', 'nykaa.com'],
    keywords: ['makeup', 'cosmetics', 'skincare', 'perfume', 'beauty']
  },
  sports: {
    domains: ['decathlon.com', 'sportsdirect.com'],
    keywords: ['sports', 'fitness', 'gym', 'exercise', 'workout', 'yoga']
  },
  general: {
    domains: [],
    keywords: []
  }
};

// Category-specific prompts
const CATEGORY_PROMPTS: Record<ProductCategory, CategoryPrompts> = {
  fashion: {
    notesPlaceholder: 'Size, color, fit preference (e.g., M, Black, Slim fit)',
    tips: ['Specify exact size (S/M/L/XL)', 'Mention preferred color', 'Include fit type if applicable']
  },
  footwear: {
    notesPlaceholder: 'Size (US/UK/EU), color, width (e.g., US 10, Black, Regular)',
    tips: ['Include size system (US/UK/EU)', 'Specify width if needed', 'Mention color preference']
  },
  electronics: {
    notesPlaceholder: 'Model, storage, color (e.g., 256GB, Space Gray, WiFi only)',
    tips: ['Specify exact model number', 'Include storage capacity', 'Mention color if multiple options']
  },
  books: {
    notesPlaceholder: 'Edition, format preference (e.g., Latest edition, Hardcover)',
    tips: ['Specify edition if important', 'Choose format (hardcover/paperback)', 'Include ISBN if known']
  },
  home: {
    notesPlaceholder: 'Color, size, material preference (e.g., White, Queen size, Cotton)',
    tips: ['Specify dimensions/size', 'Mention color preference', 'Include material if options exist']
  },
  beauty: {
    notesPlaceholder: 'Shade, size, type (e.g., Shade 02, 50ml, Dry skin)',
    tips: ['Include shade/color number', 'Specify size/volume', 'Mention skin type if relevant']
  },
  sports: {
    notesPlaceholder: 'Size, color, specific model (e.g., L, Blue, Pro version)',
    tips: ['Include size for apparel', 'Specify model variations', 'Mention color preference']
  },
  luxury: {
    notesPlaceholder: 'Size, color, material preference (e.g., Medium, Black, Leather)',
    tips: ['Luxury items often have limited sizes', 'Specify color carefully', 'Material choice affects price', 'Authentication may be required']
  },
  toys: {
    notesPlaceholder: 'Age range, color preference, specific features (e.g., 3-6 years, Pink, LED lights)',
    tips: ['Check age recommendations', 'Mention color if multiple options', 'Include special features needed', 'Safety certifications important']
  },
  baby: {
    notesPlaceholder: 'Size, color, organic preference (e.g., 6M, Neutral, Organic cotton)',
    tips: ['Baby sizes change quickly', 'Organic materials preferred', 'Consider growth room', 'Safety first for all items']
  },
  general: {
    notesPlaceholder: 'Size, color, variant, or any specific requirements',
    tips: ['Add any specific details', 'Include variant information', 'Mention special requirements']
  }
};

// Alternative domain suggestions
const DOMAIN_ALTERNATIVES: Record<string, Record<string, string>> = {
  'amazon.com': {
    'IN': 'amazon.in',
    'GB': 'amazon.co.uk',
    'CA': 'amazon.ca',
    'DE': 'amazon.de',
    'JP': 'amazon.co.jp'
  },
  'ebay.com': {
    'GB': 'ebay.co.uk',
    'DE': 'ebay.de',
    'CA': 'ebay.ca',
    'AU': 'ebay.com.au',
    'IN': 'ebay.in'
  }
};

class UrlAnalysisService {
  // Performance optimization: Cache analysis results
  private analysisCache = new Map<string, UrlAnalysis>();
  private readonly CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps = new Map<string, number>();

  /**
   * Analyze a URL to extract domain, suggested country, and category with confidence scoring
   * Results are cached for performance
   */
  analyzeUrl(url: string): UrlAnalysis {
    // Check cache first
    const cacheKey = url.toLowerCase().trim();
    const cachedResult = this.getCachedAnalysis(cacheKey);
    if (cachedResult) {
      
      return cachedResult;
    }
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '').toLowerCase();
      
      // Enhanced country detection with confidence scoring
      const detectionResult = this.detectCountryWithConfidence(url, domain);
      
      // Detect category
      const category = this.detectCategory(url, domain);
      
      // Get category-specific prompts
      const categoryPrompts = category ? CATEGORY_PROMPTS[category] : undefined;
      
      const result: UrlAnalysis = {
        domain,
        suggestedCountry: detectionResult.country,
        category,
        confidence: detectionResult.confidence,
        detectionMethod: detectionResult.method,
        categoryPrompts
      };

      // Cache the result for performance
      this.setCachedAnalysis(cacheKey, result);
      
      return result;
    } catch (error) {
      // Invalid URL - cache this too to avoid repeated parsing attempts
      const errorResult: UrlAnalysis = {
        domain: '',
        suggestedCountry: null,
        category: null,
        confidence: 'low',
        detectionMethod: 'domain'
      };
      
      this.setCachedAnalysis(cacheKey, errorResult);
      return errorResult;
    }
  }

  /**
   * Cache helper methods for performance optimization
   */
  private getCachedAnalysis(cacheKey: string): UrlAnalysis | null {
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp || Date.now() - timestamp > this.CACHE_EXPIRY) {
      // Cache expired or doesn't exist
      this.analysisCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
      return null;
    }
    
    return this.analysisCache.get(cacheKey) || null;
  }

  private setCachedAnalysis(cacheKey: string, result: UrlAnalysis): void {
    this.analysisCache.set(cacheKey, result);
    this.cacheTimestamps.set(cacheKey, Date.now());
    
    // Cleanup old cache entries if cache gets too large
    if (this.analysisCache.size > 100) {
      const oldestEntries = Array.from(this.cacheTimestamps.entries())
        .sort((a, b) => a[1] - b[1])
        .slice(0, 20); // Remove oldest 20 entries
      
      oldestEntries.forEach(([key]) => {
        this.analysisCache.delete(key);
        this.cacheTimestamps.delete(key);
      });
    }
  }

  /**
   * TLD to country fallback mapping for enhanced coverage
   */
  private static readonly TLD_FALLBACK_MAP: Record<string, string> = {
    // Country-code TLDs (ccTLDs)
    'co.uk': 'GB', 'uk': 'GB',
    'co.jp': 'JP', 'jp': 'JP',
    'com.au': 'AU', 'au': 'AU',
    'co.in': 'IN', 'in': 'IN',
    'ca': 'CA', 'com.ca': 'CA',
    'de': 'DE', 'com.de': 'DE',
    'fr': 'FR', 'com.fr': 'FR',
    'it': 'IT', 'com.it': 'IT',
    'es': 'ES', 'com.es': 'ES',
    'nl': 'NL', 'com.nl': 'NL',
    'be': 'BE', 'com.be': 'BE',
    'ch': 'CH', 'com.ch': 'CH',
    'at': 'AT', 'com.at': 'AT',
    'se': 'SE', 'com.se': 'SE',
    'dk': 'DK', 'com.dk': 'DK',
    'no': 'NO', 'com.no': 'NO',
    'fi': 'FI', 'com.fi': 'FI',
    'pl': 'PL', 'com.pl': 'PL',
    'ru': 'RU', 'com.ru': 'RU',
    'cn': 'CN', 'com.cn': 'CN',
    'kr': 'KR', 'co.kr': 'KR',
    'hk': 'HK', 'com.hk': 'HK',
    'sg': 'SG', 'com.sg': 'SG',
    'my': 'MY', 'com.my': 'MY',
    'th': 'TH', 'co.th': 'TH',
    'ph': 'PH', 'com.ph': 'PH',
    'id': 'ID', 'co.id': 'ID',
    'vn': 'VN', 'com.vn': 'VN',
    'tw': 'TW', 'com.tw': 'TW',
    'nz': 'NZ', 'co.nz': 'NZ',
    'za': 'ZA', 'co.za': 'ZA',
    'ae': 'AE', 'com.ae': 'AE',
    'sa': 'SA', 'com.sa': 'SA',
    'eg': 'EG', 'com.eg': 'EG',
    'ma': 'MA', 'com.ma': 'MA',
    'ng': 'NG', 'com.ng': 'NG',
    'ke': 'KE', 'co.ke': 'KE',
    'br': 'BR', 'com.br': 'BR',
    'mx': 'MX', 'com.mx': 'MX',
    'ar': 'AR', 'com.ar': 'AR',
    'cl': 'CL', 'com.cl': 'CL',
    'co': 'CO', 'com.co': 'CO',
    'pe': 'PE', 'com.pe': 'PE',
    'tr': 'TR', 'com.tr': 'TR',
    'ie': 'IE', 'com.ie': 'IE',
    'pt': 'PT', 'com.pt': 'PT',
    'gr': 'GR', 'com.gr': 'GR',
    'cz': 'CZ', 'com.cz': 'CZ',
    'hu': 'HU', 'com.hu': 'HU',
    'ro': 'RO', 'com.ro': 'RO',
    'bg': 'BG', 'com.bg': 'BG',
    'hr': 'HR', 'com.hr': 'HR',
    'si': 'SI', 'com.si': 'SI',
    'sk': 'SK', 'com.sk': 'SK',
    'lt': 'LT', 'com.lt': 'LT',
    'lv': 'LV', 'com.lv': 'LV',
    'ee': 'EE', 'com.ee': 'EE',
    'ua': 'UA', 'com.ua': 'UA',
    'by': 'BY', 'com.by': 'BY',
    'kz': 'KZ', 'com.kz': 'KZ',
    'uz': 'UZ', 'com.uz': 'UZ',
    'ge': 'GE', 'com.ge': 'GE',
    'am': 'AM', 'com.am': 'AM',
    'az': 'AZ', 'com.az': 'AZ',
    'il': 'IL', 'co.il': 'IL',
    'jo': 'JO', 'com.jo': 'JO',
    'lb': 'LB', 'com.lb': 'LB',
    'qa': 'QA', 'com.qa': 'QA',
    'kw': 'KW', 'com.kw': 'KW',
    'bh': 'BH', 'com.bh': 'BH',
    'om': 'OM', 'com.om': 'OM',
    'pk': 'PK', 'com.pk': 'PK',
    'bd': 'BD', 'com.bd': 'BD',
    'lk': 'LK', 'com.lk': 'LK',
    'np': 'NP', 'com.np': 'NP',
    'mm': 'MM', 'com.mm': 'MM',
    'kh': 'KH', 'com.kh': 'KH',
    'la': 'LA', 'com.la': 'LA',
    'mn': 'MN', 'com.mn': 'MN'
  };

  /**
   * Enhanced country detection with confidence scoring and method tracking
   */
  private detectCountryWithConfidence(url: string, domain: string): {
    country: string | null;
    confidence: 'high' | 'medium' | 'low';
    method: 'domain' | 'pattern' | 'tld' | 'marketplace';
  } {
    const urlLower = url.toLowerCase();

    // Layer 1: Regional marketplace patterns (High confidence)
    const regionalMarketplace = this.detectRegionalMarketplace(urlLower, domain);
    if (regionalMarketplace) {
      return {
        country: regionalMarketplace,
        confidence: 'high',
        method: 'marketplace'
      };
    }

    // Layer 2: Exact domain mapping (High confidence)
    if (DOMAIN_COUNTRY_MAP[domain]) {
      return {
        country: DOMAIN_COUNTRY_MAP[domain],
        confidence: 'high',
        method: 'domain'
      };
    }

    // Layer 3: Brand-specific URL patterns (Medium confidence)
    const patternResult = this.detectCountryFromUrlPatterns(urlLower, domain);
    if (patternResult) {
      return {
        country: patternResult,
        confidence: 'medium',
        method: 'pattern'
      };
    }

    // Layer 4: TLD fallback detection (Low confidence)
    const tldCountry = this.detectCountryFromTLD(domain);
    if (tldCountry) {
      return {
        country: tldCountry,
        confidence: 'low',
        method: 'tld'
      };
    }
    
    return {
      country: null,
      confidence: 'low',
      method: 'domain'
    };
  }

  /**
   * Brand-specific URL pattern detection (for Layer 3)
   */
  private detectCountryFromUrlPatterns(urlLower: string, domain: string): string | null {
    // H&M country detection (pattern: en_{country_code})
    if (domain.includes('hm.com')) {
      // Primary pattern: /en_{country_code}/ (e.g., /en_in/, /en_us/, /en_gb/)
      const hmCountryMatch = urlLower.match(/\/en_([a-z]{2})/);
      if (hmCountryMatch) {
        return hmCountryMatch[1].toUpperCase();
      }
      
      // Alternative pattern for non-English locales (e.g., /de_de/, /fr_fr/)
      const hmLocaleMatch = urlLower.match(/\/([a-z]{2})_([a-z]{2})/);
      if (hmLocaleMatch) {
        return hmLocaleMatch[2].toUpperCase(); // Return country code (second part)
      }
      
      // Default to Sweden if no pattern matches (H&M headquarters)
      return 'SE';
    }

    // ASOS country detection (pattern: /country-code/)
    if (domain.includes('asos.com')) {
      // ASOS URLs like: https://www.asos.com/us/, https://www.asos.com/fr/, etc.
      const asosCountryMatch = urlLower.match(/asos\.com\/([a-z]{2})\//);
      if (asosCountryMatch) {
        return asosCountryMatch[1].toUpperCase();
      }
      
      // Fallback patterns for ASOS regional URLs
      if (urlLower.includes('/us/')) return 'US';
      if (urlLower.includes('/gb/')) return 'GB';
      if (urlLower.includes('/fr/')) return 'FR';
      if (urlLower.includes('/de/')) return 'DE';
      if (urlLower.includes('/es/')) return 'ES';
      if (urlLower.includes('/it/')) return 'IT';
      if (urlLower.includes('/au/')) return 'AU';
      if (urlLower.includes('/ca/')) return 'CA';
      if (urlLower.includes('/nl/')) return 'NL';
      if (urlLower.includes('/se/')) return 'SE';
      if (urlLower.includes('/dk/')) return 'DK';
      return 'GB'; // ASOS default (UK-based company)
    }

    // Amazon country detection (existing logic from BrightDataProductService)
    if (domain.includes('amazon.')) {
      // Amazon regional patterns like /dp/, /gp/product/, etc.
      const domainParts = domain.split('.');
      const tld = domainParts[domainParts.length - 1];
      
      // Map common Amazon TLDs to countries
      const amazonTldMap: Record<string, string> = {
        'com': 'US',
        'co.uk': 'GB', 
        'ca': 'CA',
        'de': 'DE',
        'fr': 'FR',
        'es': 'ES',
        'it': 'IT',
        'co.jp': 'JP',
        'in': 'IN',
        'com.au': 'AU',
        'ae': 'AE',
        'sa': 'SA',
        'com.br': 'BR',
        'com.mx': 'MX',
        'sg': 'SG',
        'nl': 'NL'
      };

      // Handle multi-part TLDs
      const fullTld = domainParts.slice(1).join('.');
      if (amazonTldMap[fullTld]) {
        return amazonTldMap[fullTld];
      }
      if (amazonTldMap[tld]) {
        return amazonTldMap[tld];
      }
    }

    // Zara country detection (pattern: /country/en/ or /country/)
    if (domain.includes('zara.com')) {
      // Zara regional URLs like:
      // https://www.zara.com/in/en/
      // https://www.zara.com/us/
      // https://www.zara.com/uk/
      const zaraCountryMatch = urlLower.match(/zara\.com\/([a-z]{2})(?:\/|$)/);
      if (zaraCountryMatch) {
        const countryCode = zaraCountryMatch[1].toUpperCase();
        // Map some special cases
        if (countryCode === 'UK') return 'GB'; // UK -> GB for consistency
        return countryCode;
      }
      
      // Fallback patterns for Zara regional URLs
      if (urlLower.includes('/in/en/')) return 'IN';
      if (urlLower.includes('/us/')) return 'US';
      if (urlLower.includes('/uk/')) return 'GB';
      if (urlLower.includes('/de/')) return 'DE';
      if (urlLower.includes('/fr/')) return 'FR';
      if (urlLower.includes('/es/')) return 'ES';
      if (urlLower.includes('/it/')) return 'IT';
      if (urlLower.includes('/ca/')) return 'CA';
      if (urlLower.includes('/au/')) return 'AU';
      if (urlLower.includes('/jp/')) return 'JP';
      if (urlLower.includes('/kr/')) return 'KR';
      if (urlLower.includes('/mx/')) return 'MX';
      if (urlLower.includes('/br/')) return 'BR';
      if (urlLower.includes('/ar/')) return 'AR';
      if (urlLower.includes('/cl/')) return 'CL';
      if (urlLower.includes('/co/')) return 'CO';
      if (urlLower.includes('/pe/')) return 'PE';
      return 'ES'; // Zara default (Spanish company)
    }

    // Nike country detection (pattern: /country-code/)
    if (domain.includes('nike.com')) {
      // Nike regional URLs like: https://www.nike.com/gb/, https://www.nike.com/in/, etc.
      const nikeCountryMatch = urlLower.match(/nike\.com\/([a-z]{2})(?:\/|$)/);
      if (nikeCountryMatch) {
        const countryCode = nikeCountryMatch[1].toUpperCase();
        // Map some special cases
        if (countryCode === 'GB') return 'GB';
        if (countryCode === 'IN') return 'IN';
        if (countryCode === 'CA') return 'CA';
        if (countryCode === 'DE') return 'DE';
        if (countryCode === 'FR') return 'FR';
        if (countryCode === 'JP') return 'JP';
        if (countryCode === 'AU') return 'AU';
        return countryCode;
      }
      return 'US'; // Nike default (US company)
    }

    // Adidas country detection (similar pattern)
    if (domain.includes('adidas.com')) {
      const adidasCountryMatch = urlLower.match(/adidas\.com\/([a-z]{2})(?:\/|$)/);
      if (adidasCountryMatch) {
        return adidasCountryMatch[1].toUpperCase();
      }
      return 'DE'; // Adidas default (German company)
    }

    // Uniqlo country detection
    if (domain.includes('uniqlo.com')) {
      // Uniqlo has regional sites like uniqlo.com/us/, uniqlo.com/uk/, etc.
      const uniqloCountryMatch = urlLower.match(/uniqlo\.com\/([a-z]{2})(?:\/|$)/);
      if (uniqloCountryMatch) {
        const countryCode = uniqloCountryMatch[1].toUpperCase();
        if (countryCode === 'UK') return 'GB'; // UK -> GB for consistency
        return countryCode;
      }
      return 'JP'; // Uniqlo default (Japanese company)
    }
    
    return null;
  }

  /**
   * Detect regional marketplace patterns for Southeast Asia and other regions
   */
  private detectRegionalMarketplace(urlLower: string, domain: string): string | null {
    // Shopee regional detection (enhanced patterns)
    if (domain.includes('shopee.')) {
      if (domain === 'shopee.sg' || urlLower.includes('shopee.sg')) return 'SG';
      if (domain === 'shopee.co.id' || urlLower.includes('shopee.co.id')) return 'ID';
      if (domain === 'shopee.co.th' || urlLower.includes('shopee.co.th')) return 'TH';
      if (domain === 'shopee.ph' || urlLower.includes('shopee.ph')) return 'PH';
      if (domain === 'shopee.vn' || urlLower.includes('shopee.vn')) return 'VN';
      if (domain === 'shopee.my' || urlLower.includes('shopee.my')) return 'MY';
      if (domain === 'shopee.tw' || urlLower.includes('shopee.tw')) return 'TW';
      // Default to Singapore (Shopee headquarters)
      return 'SG';
    }

    // Lazada regional detection
    if (domain.includes('lazada.')) {
      if (domain === 'lazada.sg' || urlLower.includes('lazada.sg')) return 'SG';
      if (domain === 'lazada.co.id' || urlLower.includes('lazada.co.id')) return 'ID';
      if (domain === 'lazada.co.th' || urlLower.includes('lazada.co.th')) return 'TH';
      if (domain === 'lazada.com.ph' || urlLower.includes('lazada.com.ph')) return 'PH';
      if (domain === 'lazada.vn' || urlLower.includes('lazada.vn')) return 'VN';
      if (domain === 'lazada.com.my' || urlLower.includes('lazada.com.my')) return 'MY';
      return 'SG'; // Default to Singapore
    }

    // Mercado Libre regional detection
    if (domain.includes('mercadolibre.') || domain.includes('mercadolivre.')) {
      if (domain.includes('.com.ar') || urlLower.includes('argentina')) return 'AR';
      if (domain.includes('.com.mx') || urlLower.includes('mexico')) return 'MX';
      if (domain.includes('.com.br') || urlLower.includes('brasil')) return 'BR';
      if (domain.includes('.cl') || urlLower.includes('chile')) return 'CL';
      if (domain.includes('.com.co') || urlLower.includes('colombia')) return 'CO';
      if (domain.includes('.com.pe') || urlLower.includes('peru')) return 'PE';
      if (domain.includes('.com.uy') || urlLower.includes('uruguay')) return 'UY';
      if (domain.includes('.com.ve') || urlLower.includes('venezuela')) return 'VE';
      return 'AR'; // Default to Argentina (original market)
    }

    // Jumia regional detection (Africa)
    if (domain.includes('jumia.')) {
      if (domain.includes('.com.ng') || domain === 'jumia.ng') return 'NG';
      if (domain.includes('.co.ke') || domain === 'jumia.ke') return 'KE';
      if (domain.includes('.com.eg') || domain === 'jumia.eg') return 'EG';
      if (domain.includes('.ma') || domain === 'jumia.ma') return 'MA';
      if (domain.includes('.co.za') || domain === 'jumia.za') return 'ZA';
      if (domain.includes('.com.gh') || domain === 'jumia.gh') return 'GH';
      if (domain.includes('.ci') || domain === 'jumia.ci') return 'CI';
      if (domain.includes('.sn') || domain === 'jumia.sn') return 'SN';
      if (domain.includes('.ug') || domain === 'jumia.ug') return 'UG';
      if (domain.includes('.tn') || domain === 'jumia.tn') return 'TN';
      return 'NG'; // Default to Nigeria
    }

    // Zalando regional detection (enhanced)
    if (domain.includes('zalando.')) {
      if (domain === 'zalando.de' || domain === 'zalando.com') return 'DE';
      if (domain === 'zalando.fr') return 'FR';
      if (domain === 'zalando.co.uk') return 'GB';
      if (domain === 'zalando.it') return 'IT';
      if (domain === 'zalando.es') return 'ES';
      if (domain === 'zalando.nl') return 'NL';
      if (domain === 'zalando.be') return 'BE';
      if (domain === 'zalando.at') return 'AT';
      if (domain === 'zalando.ch') return 'CH';
      if (domain === 'zalando.pl') return 'PL';
      if (domain === 'zalando.se') return 'SE';
      if (domain === 'zalando.dk') return 'DK';
      if (domain === 'zalando.fi') return 'FI';
      if (domain === 'zalando.no') return 'NO';
      return 'DE'; // Default to Germany (headquarters)
    }

    // Sephora regional detection
    if (domain.includes('sephora.')) {
      if (domain === 'sephora.com') return 'US';
      if (domain === 'sephora.ca') return 'CA';
      if (domain === 'sephora.fr') return 'FR';
      if (domain === 'sephora.it') return 'IT';
      if (domain === 'sephora.com.au') return 'AU';
      if (domain === 'sephora.sg') return 'SG';
      if (domain === 'sephora.my') return 'MY';
      if (domain === 'sephora.th') return 'TH';
      if (domain === 'sephora.com.br') return 'BR';
      if (domain === 'sephora.ae') return 'AE';
      if (domain === 'sephora.sa') return 'SA';
      return 'US'; // Default to US
    }

    // Wayfair regional detection
    if (domain.includes('wayfair.')) {
      if (domain === 'wayfair.com') return 'US';
      if (domain === 'wayfair.ca') return 'CA';
      if (domain === 'wayfair.co.uk') return 'GB';
      if (domain === 'wayfair.de') return 'DE';
      return 'US'; // Default to US
    }

    return null;
  }

  /**
   * Detect country from TLD as fallback
   */
  private detectCountryFromTLD(domain: string): string | null {
    const domainParts = domain.split('.');
    
    // Handle multi-part TLDs (e.g., co.uk, com.au)
    if (domainParts.length >= 3) {
      const tld = domainParts.slice(-2).join('.');
      if (UrlAnalysisService.TLD_FALLBACK_MAP[tld]) {
        return UrlAnalysisService.TLD_FALLBACK_MAP[tld];
      }
    }
    
    // Handle single TLD
    const lastPart = domainParts[domainParts.length - 1];
    if (UrlAnalysisService.TLD_FALLBACK_MAP[lastPart]) {
      return UrlAnalysisService.TLD_FALLBACK_MAP[lastPart];
    }
    
    return null;
  }
  
  /**
   * Convert URL to country-specific format for supported platforms
   * This is used to ensure scraping works with the correct regional site
   */
  processUrlForCountry(url: string, targetCountry: string): string {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '').toLowerCase();
      
      // Zara URL processing
      if (domain.includes('zara.com')) {
        return this.processZaraUrlForCountry(url, targetCountry);
      }
      
      // H&M URL processing (if needed)
      if (domain.includes('hm.com')) {
        return this.processHMUrlForCountry(url, targetCountry);
      }
      
      // Return original URL if no processing needed
      return url;
    } catch (error) {
      console.warn('URL processing failed:', error);
      return url;
    }
  }

  /**
   * Process Zara URL to ensure correct regional format
   */
  private processZaraUrlForCountry(url: string, targetCountry: string): string {
    try {
      const urlObj = new URL(url);
      
      // Map target country to Zara regional codes
      const countryToZaraRegion: Record<string, string> = {
        'IN': 'in/en',
        'US': 'us',
        'GB': 'uk',
        'CA': 'ca',
        'AU': 'au',
        'DE': 'de',
        'FR': 'fr',
        'ES': 'es',
        'IT': 'it',
        'JP': 'jp',
        'KR': 'kr',
        'MX': 'mx',
        'BR': 'br',
        'AR': 'ar',
        'CL': 'cl',
        'CO': 'co',
        'PE': 'pe'
      };
      
      const zaraRegion = countryToZaraRegion[targetCountry] || 'us'; // Default to US
      
      // Check if URL already has correct region
      if (url.includes(`zara.com/${zaraRegion}/`)) {
        return url;
      }
      
      // Remove existing region from path if any
      let pathname = urlObj.pathname;
      // Remove patterns like /in/en/, /us/, /uk/, etc.
      pathname = pathname.replace(/^\/[a-z]{2}(?:\/[a-z]{2})?\//, '/');
      
      // Construct new URL with correct region
      const newUrl = `${urlObj.protocol}//${urlObj.hostname}/${zaraRegion}${pathname}${urlObj.search}${urlObj.hash}`;
      
      
      return newUrl;
    } catch (error) {
      console.warn('Zara URL processing failed:', error);
      return url;
    }
  }

  /**
   * Process H&M URL to ensure correct regional format
   */
  private processHMUrlForCountry(url: string, targetCountry: string): string {
    // H&M URL processing logic can be added here if needed
    return url;
  }

  /**
   * Get alternative domain suggestion for a different country
   */
  getAlternativeDomain(currentDomain: string, targetCountry: string): string | null {
    const baseDomain = currentDomain.replace('www.', '').toLowerCase();
    const alternatives = DOMAIN_ALTERNATIVES[baseDomain];
    
    if (alternatives && alternatives[targetCountry]) {
      return alternatives[targetCountry];
    }
    
    // Check if it's an Amazon or eBay domain and suggest the right one
    if (baseDomain.includes('amazon.') && targetCountry === 'IN') {
      return 'amazon.in';
    }
    if (baseDomain.includes('amazon.') && targetCountry === 'US') {
      return 'amazon.com';
    }
    
    return null;
  }
  
  /**
   * Detect product category from URL and domain
   */
  private detectCategory(url: string, domain: string): ProductCategory | null {
    const lowerUrl = url.toLowerCase();
    
    // Check domain-based categories first
    for (const [category, patterns] of Object.entries(CATEGORY_PATTERNS)) {
      if (patterns.domains.includes(domain)) {
        return category as ProductCategory;
      }
    }
    
    // Check URL keywords
    for (const [category, patterns] of Object.entries(CATEGORY_PATTERNS)) {
      for (const keyword of patterns.keywords) {
        if (lowerUrl.includes(keyword)) {
          return category as ProductCategory;
        }
      }
    }
    
    // Special case: Amazon category detection
    if (domain.includes('amazon')) {
      if (lowerUrl.includes('/dp/b0') || lowerUrl.includes('electronics')) {
        return 'electronics';
      }
      if (lowerUrl.includes('clothing') || lowerUrl.includes('apparel')) {
        return 'fashion';
      }
      if (lowerUrl.includes('shoes') || lowerUrl.includes('footwear')) {
        return 'footwear';
      }
      if (lowerUrl.includes('books')) {
        return 'books';
      }
    }
    
    return 'general';
  }
  
  /**
   * Check if selected country matches the URL domain
   */
  checkCountryMatch(url: string, selectedCountry: string): {
    matches: boolean;
    suggestedCountry?: string;
    alternativeDomain?: string;
  } {
    const analysis = this.analyzeUrl(url);
    
    if (!analysis.suggestedCountry) {
      // Can't determine country from URL
      return { matches: true };
    }
    
    const matches = analysis.suggestedCountry === selectedCountry;
    
    if (!matches) {
      const alternativeDomain = this.getAlternativeDomain(analysis.domain, selectedCountry);
      
      return {
        matches: false,
        suggestedCountry: analysis.suggestedCountry,
        alternativeDomain
      };
    }
    
    return { matches: true };
  }
}

// Export singleton instance
export const urlAnalysisService = new UrlAnalysisService();